import { z } from 'zod';
import { KbCategorySchema, MeetingStatusSchema, OutcomeSchema, PlatformSchema, RoleSchema, } from './domain.js';
import { FollowUpEmailSchema, NextStepSchema } from './postcall.js';
import { SentimentLevelSchema, } from './sentiment.js';
/**
 * REST API contract (all routes are prefixed with /v1). Request bodies are zod
 * schemas (validated by the server); responses are plain TypeScript types.
 * See docs/api.md for the route list.
 */
// ---------- Auth ----------
export const SignupBodySchema = z.object({
    email: z.string().email().max(254),
    password: z.string().min(10).max(200),
    name: z.string().min(1).max(120),
});
export const LoginBodySchema = z.object({
    email: z.string().email(),
    password: z.string().min(1).max(200),
    /** 'web' = admin panel: refresh token goes into an httpOnly cookie instead of the body. */
    client: z.enum(['desktop', 'web']).default('desktop'),
});
export const RefreshBodySchema = z.object({ refreshToken: z.string().optional() });
// ---------- Clients & contacts ----------
export const ClientBodySchema = z.object({
    name: z.string().min(1).max(200),
    domains: z.array(z.string().min(3).max(253)).max(20).default([]),
    industry: z.string().max(120).nullable().optional(),
    notes: z.string().max(10000).nullable().optional(),
    ownerId: z.string().nullable().optional(),
});
export const ClientPatchSchema = ClientBodySchema.partial();
export const ContactBodySchema = z.object({
    name: z.string().max(200).default(''),
    email: z.string().email().nullable().optional(),
    title: z.string().max(200).nullable().optional(),
});
export const MeetingListQuerySchema = z.object({
    q: z.string().max(200).optional(),
    repId: z.string().optional(),
    teamId: z.string().optional(),
    clientId: z.string().optional(),
    platform: PlatformSchema.optional(),
    outcome: OutcomeSchema.optional(),
    status: MeetingStatusSchema.optional(),
    sentiment: SentimentLevelSchema.optional(),
    from: z.string().optional(),
    to: z.string().optional(),
    sort: z.enum(['startedAt', 'durationSec', 'title', 'client', 'rep']).default('startedAt'),
    order: z.enum(['asc', 'desc']).default('desc'),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
    /** 'mine' limits results to the caller's own meetings regardless of role. */
    scope: z.enum(['visible', 'mine']).default('visible'),
});
export const MeetingPatchSchema = z.object({
    title: z.string().min(1).max(300).optional(),
    outcome: OutcomeSchema.nullable().optional(),
    tags: z.array(z.string().max(40)).max(20).optional(),
    clientId: z.string().nullable().optional(),
    platform: PlatformSchema.optional(),
});
export const SummaryPatchSchema = z.object({
    summary: z.array(z.string()).optional(),
    nextSteps: z.array(NextStepSchema).optional(),
    painPoints: z.array(z.string()).optional(),
    requirements: z.array(z.string()).optional(),
    objections: z.array(z.string()).optional(),
    followUpEmail: FollowUpEmailSchema.optional(),
});
// ---------- Users & teams ----------
export const UserPatchSchema = z.object({
    name: z.string().min(1).max(120).optional(),
    role: RoleSchema.optional(),
    teamId: z.string().nullable().optional(),
    active: z.boolean().optional(),
    monthlyLimitUsd: z.number().min(0).max(100000).nullable().optional(),
    cloudSttAllowed: z.boolean().optional(),
});
export const InviteBodySchema = z.object({
    email: z.string().email(),
    name: z.string().min(1).max(120),
    role: RoleSchema.default('rep'),
    teamId: z.string().nullable().optional(),
});
export const TeamBodySchema = z.object({ name: z.string().min(1).max(120) });
// ---------- Knowledge base ----------
export const KbDocBodySchema = z.object({
    title: z.string().min(1).max(200),
    category: KbCategorySchema,
    content: z.string().min(1).max(500_000),
    active: z.boolean().default(true),
});
export const KbDocPatchSchema = z.object({
    title: z.string().min(1).max(200).optional(),
    category: KbCategorySchema.optional(),
    /** Supplying content creates a new version. */
    content: z.string().min(1).max(500_000).optional(),
    active: z.boolean().optional(),
});
// ---------- Settings ----------
export const SettingsSchema = z.object({
    transcriptRetentionDays: z.number().int().min(1).max(3650),
    summaryRetentionDays: z.number().int().min(1).max(3650),
    defaultLanguage: z.string().min(2).max(10),
    suggestionIntervalSec: z.number().int().min(10).max(120),
    defaultMonthlyLimitUsd: z.number().min(0).max(100000),
    softLimitPct: z.number().int().min(10).max(100),
    askBufferUsd: z.number().min(0).max(1000),
    /** Empty = anyone may sign up. Otherwise only these email domains. */
    allowedSignupDomains: z.array(z.string()).max(50),
    /** Email domains treated as "our side" when matching calendar attendees. */
    internalDomains: z.array(z.string()).max(50),
});
//# sourceMappingURL=api.js.map